////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.6                                                            //
//  Summary: Cloudflare Turnstile loader — explicit render, no npm dep       //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

// Script-tag integration by design (1.0.6.md §1.2 / hard rule: no new deps).
// Loaded on demand — the script is only fetched after a visitor's first
// interaction with a Turnstile-protected form, never on page load.

export interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  /* 'interaction-only' keeps the widget invisible unless a challenge actually
   * needs the visitor to do something — managed mode still mints tokens
   * silently in the background (CF render params, 14/08/2026). */
  appearance?: 'always' | 'execute' | 'interaction-only';
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    __irnTurnstileOnLoad?: () => void;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__irnTurnstileOnLoad&render=explicit';

let pending: Promise<TurnstileApi> | null = null;

export function loadTurnstile (): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  pending ??= new Promise<TurnstileApi>((resolve, reject) => {
    window.__irnTurnstileOnLoad = () => {
      delete window.__irnTurnstileOnLoad;
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded without exposing its API'));
    };
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onerror = () => {
      pending = null;
      delete window.__irnTurnstileOnLoad;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });

  return pending;
}

/** Tokens are single-use — after any failed submit the widget must be reset to
 * mint a fresh one. Safe no-op when the widget/script never loaded. */
export function resetTurnstile (widgetId: string | null): void {
  if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
}
